import { QueryClient, type QueryFunctionContext, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"
import isNull from "lodash-es/isNull"

import { axiosClient } from "$/http"

import { extractStringFromQueryKeyPart, hours, seconds } from "@/3514/utils"

import { EParticipantSurveyQueryKey } from "../constants"
import { Survey, SurveyDataApi, SurveyStatus } from "../models"

import { Urls } from "./urls"

async function fetcher({
    queryKey
}: QueryFunctionContext<ReturnType<typeof EParticipantSurveyQueryKey>>): Promise<SurveyDataApi> {
    const accessCode: string = extractStringFromQueryKeyPart(queryKey[1])

    if (isNull(accessCode)) throw new Error("Access code can't be null")

    const { data } = await axiosClient.get(Urls.surveyAssignment(accessCode))

    return data
}

function selectSurvey(
    data: SurveyDataApi & { message?: SurveyStatus; isSelfAssessment?: boolean }
): Survey & { message?: SurveyStatus; isSelfAssessment?: boolean } {
    const survey: Survey = new Survey(data)

    if (data.message) return { ...survey, message: data.message, isSelfAssessment: data.is_self_assessment }

    return survey
}

export function useRespondentSurvey(accessCode: string) {
    return useQuery<SurveyDataApi, AxiosError, Survey & { message?: SurveyStatus; isSelfAssessment?: boolean }>({
        queryKey: EParticipantSurveyQueryKey(accessCode),
        queryFn: fetcher,
        select: selectSurvey,
        staleTime: hours(1),
        gcTime: hours(1),
        refetchOnReconnect: true,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        retry: 1,
        retryDelay: seconds(3),
        throwOnError: false,
        notifyOnChangeProps: ["data", "isLoading", "isSuccess", "isError", "isFetching", "error", "refetch"],
        experimental_prefetchInRender: true
    })
}

async function participantSurveyQueryPrefetcher(
    { qc }: { qc: QueryClient },
    { code }: { code: string }
): Promise<void> {
    await qc.prefetchQuery({
        queryKey: EParticipantSurveyQueryKey(code),
        queryFn: fetcher,
        staleTime: hours(1),
        gcTime: hours(1),
        retry: false
    })
}

export { participantSurveyQueryPrefetcher }
